import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Eye, EyeOff, FlaskConical, Loader2, Pencil, PlayCircle, Save, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useTestApiEndpoint } from "@/hooks/use-test-api-endpoint";
import {
  useOrgApiEnvironment,
  useUpsertOrgApiEnvironment,
  type ApiEnvironment,
  type OrgApiEnvironmentInput,
} from "@/hooks/use-org-api-environment";

interface EnvironmentCredentialsCardProps {
  organizationId: string;
  environment: ApiEnvironment;
  title: string;
  description: string;
  canEdit: boolean;
}

const maskToken = (token: string | null | undefined) => {
  if (!token) return "";
  if (token.length <= 8) return "••••••••";
  return token.slice(0, 4) + "••••••••••••" + token.slice(-4);
};

const parseDevices = (csv: string | null | undefined): string[] => {
  if (!csv) return [];
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const EnvironmentCredentialsCard = ({
  organizationId,
  environment,
  title,
  description,
  canEdit,
}: EnvironmentCredentialsCardProps) => {
  const { data: row, isLoading } = useOrgApiEnvironment(organizationId, environment);
  const upsert = useUpsertOrgApiEnvironment();
  const testEndpointMutation = useTestApiEndpoint();

  const [isEditing, setIsEditing] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [showEndpointToken, setShowEndpointToken] = useState(false);
  const [selectedTestDeviceId, setSelectedTestDeviceId] = useState<string>("");
  const [form, setForm] = useState<OrgApiEnvironmentInput>({
    org_id_value: "",
    api_token: "",
    api_endpoint_url: "",
    api_endpoint_token: "",
    allocated_devices: "",
  });

  useEffect(() => {
    if (row) {
      setForm({
        org_id_value: row.org_id_value ?? "",
        api_token: row.api_token ?? "",
        api_endpoint_url: row.api_endpoint_url ?? "",
        api_endpoint_token: row.api_endpoint_token ?? "",
        allocated_devices: row.allocated_devices ?? "",
      });
    }
  }, [row]);

  const handleCopy = (value: string | null | undefined, label: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied to clipboard`);
  };

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        organizationId,
        environment,
        values: form,
      });
      toast.success(`${title} saved`);
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to save credentials");
    }
  };

  const handleCancel = () => {
    if (row) {
      setForm({
        org_id_value: row.org_id_value ?? "",
        api_token: row.api_token ?? "",
        api_endpoint_url: row.api_endpoint_url ?? "",
        api_endpoint_token: row.api_endpoint_token ?? "",
        allocated_devices: row.allocated_devices ?? "",
      });
    }
    setIsEditing(false);
  };

  const hasAnyValue =
    !!row &&
    (row.org_id_value ||
      row.api_token ||
      row.api_endpoint_url ||
      row.api_endpoint_token ||
      row.allocated_devices);

  const devices = parseDevices(row?.allocated_devices);

  const { data: resolvedDevices, isLoading: resolvingDevices } = useQuery({
    queryKey: ["env-allocated-devices", organizationId, environment, devices.join(",")],
    queryFn: async () => {
      if (devices.length === 0) return [];
      const { data, error } = await supabase
        .from("device_details")
        .select("device_id, device_serial, device_name")
        .in("device_serial", devices);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !isEditing && devices.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const handleTest = () => {
    if (!selectedTestDeviceId) {
      toast.error("Please select a device to test with");
      return;
    }
    testEndpointMutation.mutate({ deviceId: selectedTestDeviceId });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {canEdit && !isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            {hasAnyValue ? "Edit" : "Add credentials"}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading credentials…</p>
        )}

        {!isLoading && !hasAnyValue && !isEditing && (
          <p className="text-sm text-muted-foreground">
            No credentials configured for this environment yet.
          </p>
        )}

        {!isEditing && hasAnyValue && (
          <>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Organization ID</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={row?.org_id_value ?? ""} className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => handleCopy(row?.org_id_value, "Organization ID")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">API Token</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={showApiToken ? (row?.api_token ?? "") : maskToken(row?.api_token)}
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={() => setShowApiToken((v) => !v)}>
                  {showApiToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => handleCopy(row?.api_token, "API Token")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-sm font-medium">API Endpoint URL</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={row?.api_endpoint_url ?? ""} className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => handleCopy(row?.api_endpoint_url, "Endpoint URL")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Endpoint Authentication Token</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={showEndpointToken ? (row?.api_endpoint_token ?? "") : maskToken(row?.api_endpoint_token)}
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="icon" onClick={() => setShowEndpointToken((v) => !v)}>
                  {showEndpointToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => handleCopy(row?.api_endpoint_token, "Endpoint Token")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="text-sm font-medium">Allocated Devices</Label>
              {devices.length === 0 ? (
                <p className="text-sm text-muted-foreground">No devices allocated.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {devices.map((serial) => (
                    <Badge key={serial} variant="secondary" className="font-mono">
                      {serial}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {devices.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium">Test Endpoint</h4>
                    <p className="text-sm text-muted-foreground">
                      Send test data from an allocated device to verify your {title.toLowerCase()} endpoint.
                    </p>
                  </div>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label htmlFor={`${environment}-test-device`}>Select allocated device</Label>
                      <Select
                        value={selectedTestDeviceId}
                        onValueChange={setSelectedTestDeviceId}
                        disabled={resolvingDevices}
                      >
                        <SelectTrigger id={`${environment}-test-device`}>
                          <SelectValue
                            placeholder={resolvingDevices ? "Loading devices..." : "Select a device"}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {devices.map((serial) => {
                            const match = resolvedDevices?.find(
                              (d) => d.device_serial?.toLowerCase() === serial.toLowerCase(),
                            );
                            if (!match) {
                              return (
                                <SelectItem key={serial} value={`__missing_${serial}`} disabled>
                                  {serial} (not found)
                                </SelectItem>
                              );
                            }
                            return (
                              <SelectItem key={match.device_id} value={match.device_id}>
                                {match.device_serial} — {match.device_name ?? "Unnamed"} ({match.device_id})
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleTest}
                      disabled={
                        !selectedTestDeviceId ||
                        !row?.api_endpoint_url ||
                        testEndpointMutation.isPending
                      }
                      variant="outline"
                      className="min-w-24"
                    >
                      {testEndpointMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <PlayCircle className="h-4 w-4 mr-2" />
                          Test
                        </>
                      )}
                    </Button>
                  </div>
                  {!row?.api_endpoint_url && (
                    <p className="text-sm text-muted-foreground">
                      Add an API Endpoint URL above to enable testing.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {isEditing && canEdit && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`${environment}-org-id`}>Organization ID</Label>
              <Input
                id={`${environment}-org-id`}
                value={form.org_id_value ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, org_id_value: e.target.value }))}
                className="font-mono text-sm"
                placeholder="e.g. 6d1e0952-da74-4199-8b58-bd7929f5069f"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${environment}-api-token`}>API Token</Label>
              <Input
                id={`${environment}-api-token`}
                value={form.api_token ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, api_token: e.target.value }))}
                className="font-mono text-sm"
                placeholder="Paste the API token"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${environment}-endpoint-url`}>API Endpoint URL</Label>
              <Input
                id={`${environment}-endpoint-url`}
                type="url"
                value={form.api_endpoint_url ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, api_endpoint_url: e.target.value }))}
                className="font-mono text-sm"
                placeholder="https://api.example.com/webhook"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${environment}-endpoint-token`}>Endpoint Authentication Token</Label>
              <Input
                id={`${environment}-endpoint-token`}
                value={form.api_endpoint_token ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, api_endpoint_token: e.target.value }))}
                className="font-mono text-sm"
                placeholder="Paste the webhook auth token"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`${environment}-devices`}>Allocated Devices</Label>
              <Textarea
                id={`${environment}-devices`}
                value={form.allocated_devices ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, allocated_devices: e.target.value }))}
                className="font-mono text-sm"
                placeholder="Comma-separated device serial numbers, e.g. SL-001, SL-002, SL-003"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Enter serial numbers separated by commas. They will be displayed as badges.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancel} disabled={upsert.isPending}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={upsert.isPending}
                className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
              >
                <Save className="h-4 w-4 mr-2" />
                {upsert.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};